/**
 * Maps Firebase callable errors to user-facing copy for brewhouse flows.
 * Raw codes and internal messages are logged to Sentry at call sites — never shown in UI.
 */

const TRANSIENT_CALLABLE_CODES = new Set([
  'functions/deadline-exceeded',
  'functions/unavailable',
  'functions/internal',
  'functions/cancelled',
]);

const ADD_BEER_MESSAGES = {
  transient:
    'We could not confirm the save. Your beer may still have been added — we are checking your portfolio.',
  transientAfterReconcile:
    'We could not confirm the save. If your beer appears in the portfolio, you are all set.',
  generic: 'Failed to add beer. Please try again or contact support.',
};

/** Callable codes whose messages are safe to show in the brewhouse UI. */
const SAFE_ADD_BEER_ERROR_CODES = new Set([
  'functions/invalid-argument',
  'functions/failed-precondition',
  'functions/already-exists',
  'functions/unauthenticated',
  'functions/permission-denied',
  'functions/not-found',
]);

function normalizeCallableCode(error) {
  if (typeof error?.code === 'string' && error.code.trim()) {
    return error.code.trim();
  }
  return '';
}

function isNetworkLikeMessage(message) {
  if (typeof message !== 'string' || !message.trim()) {
    return false;
  }
  const lowered = message.toLowerCase();
  return (
    lowered.includes('network')
    || lowered.includes('fetch')
    || lowered.includes('connection')
    || lowered.includes('aborted')
    || lowered.includes('timeout')
  );
}

/**
 * @param {unknown} error - Firebase FunctionsError or similar
 * @returns {boolean}
 */
export function isTransientCallableError(error) {
  const code = normalizeCallableCode(error);
  return (
    TRANSIENT_CALLABLE_CODES.has(code)
    || (code === 'functions/unknown' && isNetworkLikeMessage(error?.message))
    || (!code && isNetworkLikeMessage(error?.message))
  );
}

/**
 * @param {unknown} error
 * @param {{ operation?: string, afterReconcile?: boolean }} [options]
 * @returns {string}
 */
export function getUserFacingCallableError(error, options = {}) {
  const operation = options.operation || 'default';
  const afterReconcile = options.afterReconcile === true;

  if (operation === 'addBeer') {
    if (isTransientCallableError(error)) {
      return afterReconcile
        ? ADD_BEER_MESSAGES.transientAfterReconcile
        : ADD_BEER_MESSAGES.transient;
    }
    const code = normalizeCallableCode(error);
    const rawMessage = typeof error?.message === 'string' ? error.message.trim() : '';
    if (
      rawMessage
      && SAFE_ADD_BEER_ERROR_CODES.has(code)
      && !/^internal$/iu.test(rawMessage)
    ) {
      return rawMessage;
    }
    return ADD_BEER_MESSAGES.generic;
  }

  const rawMessage = typeof error?.message === 'string' ? error.message.trim() : '';
  return rawMessage || 'Something went wrong. Please try again.';
}

export const ADD_BEER_LOADING_LABEL = 'Checking for duplicates…';
export const ADD_BEER_RECONCILE_LABEL = 'Checking portfolio…';
